import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const ForgotPassword = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/api/auth/forgot-password', { email });
      setSubmitted(true);
      toast.success(t('toast.passwordResetSent'), { autoClose: 1000 });
    } catch (error) {
      toast.error(t('toast.passwordResetFailed'), { autoClose: 1000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={t('auth.forgot.seoTitle') || 'Forgot Password - Reset Your Account'}
        description={t('auth.forgot.seoDescription') || 'Reset your Meller Racing account password. Enter your email to receive password reset instructions.'}
        noindex={true}
      />

      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="text-center text-3xl font-display font-bold text-gray-900">
              {t('auth.forgot.title')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('auth.forgot.remember')} {' '}
              <Link to="/login" className="font-medium text-racing-red hover:underline">
                {t('auth.forgot.backToLogin')}
              </Link>
            </p>
          </div>

          {submitted ? (
            <div className="rounded-lg bg-green-50 p-6 text-center">
              <div className="text-green-800 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-green-900 mb-2">
                {t('auth.forgot.submittedTitle')}
              </h3>
              <p className="text-sm text-green-700 mb-4">
                {t('auth.forgot.submittedDesc')}
              </p>
              <p className="text-xs text-gray-600">
                {t('auth.forgot.notReceived')} {' '}
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-racing-red hover:underline font-medium"
                >
                  {t('auth.forgot.tryAgain')}
                </button>
              </p>
            </div>
          ) : (
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  {t('auth.forgot.instructions')}
                </p>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.forgot.emailLabel')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-racing-red focus:border-transparent"
                  placeholder={t('auth.forgot.emailPlaceholder')}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-racing-red text-white rounded-lg hover:bg-red-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
              >
                {loading ? (t('auth.forgot.submitting') || 'Sending...') : t('auth.forgot.submit')}
              </button>

              <div className="text-center">
                <Link
                  to="/register"
                  className="text-sm text-gray-600 hover:text-racing-red"
                >
                  {t('auth.forgot.signupPrompt')} {' '}
                  <span className="font-medium text-racing-red hover:underline">{t('auth.forgot.signupLink')}</span>
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
